import { useCallback } from 'react';
import { Box } from '@material-ui/core';
import { SButton, SConfirmDialog } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { Link, QuoteShareLink } from '@setvi/shared/interfaces';
import { Header } from '@setvi/shared/components/sdrawer-content/components/header';
import { LinkItem } from './LinkItem';
import { QuoteLinkItem } from './QuoteLinkItem';

interface ListProps {
  links: Array<Link>;
  /** The email's quote share links, listed first when it has any (DEV-785). */
  quoteLinks?: QuoteShareLink[];
  handleEdit: (item: Link) => void;
  handleEditQuoteLink?: (index: number) => void;
  handleBack?: () => void;
  handleRemoveLink(link?: Link): void;
  handleRemoveQuoteLink?: (quoteLink: QuoteShareLink) => void;
  handleClose: () => void;
}

export const List = ({
  links,
  quoteLinks,
  handleEdit,
  handleEditQuoteLink,
  handleBack: _handleBack,
  handleRemoveLink,
  handleRemoveQuoteLink,
  handleClose
}: ListProps) => {
  const { openDialog, closeDialog } = useDialog();

  // Same confirm for either kind of link — only what it removes differs.
  const confirmDelete = useCallback(
    (remove: () => void) => {
      openDialog(
        <SConfirmDialog
          type="delete"
          title="Delete link"
          description="Are you sure you want to delete this link from the email?"
          additionalMessage="The action CANNOT be reverted back"
          onClose={() => closeDialog('delete-link')}
          onConfirm={() => {
            remove();
            closeDialog('delete-link');
          }}
          confirmText="Delete & Close"
          closeText="Keep it"
        />,
        { id: 'delete-link' }
      );
    },
    [openDialog, closeDialog]
  );

  const onDelete = useCallback(
    (linkItem: Link) => confirmDelete(() => handleRemoveLink(linkItem)),
    [confirmDelete, handleRemoveLink]
  );

  return (
    <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
      <Header title="Edit & Rename Link(s)" handleClose={handleClose} />
      <Box overflow="auto" flex="1">
        {quoteLinks?.map((quoteLink, index) => (
          <QuoteLinkItem
            key={quoteLink.id}
            quoteLink={quoteLink}
            onEdit={() => handleEditQuoteLink?.(index)}
            onDelete={() =>
              confirmDelete(() => handleRemoveQuoteLink?.(quoteLink))
            }
          />
        ))}
        {links?.map(item => (
          <LinkItem
            key={item.Placeholder}
            link={item}
            onEdit={() => handleEdit(item)}
            onDelete={() => onDelete(item)}
          />
        ))}
      </Box>
      <Box mt="auto" p={2}>
        <SButton color="default" type="button" onClick={handleClose} fullWidth>
          Cancel
        </SButton>
      </Box>
    </Box>
  );
};
